import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMessages } from '../../components/Messages';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { showApiError } from '../../utils/errorUtils';
import './Users.css';
import { formatAppDate } from '../../utils/dateUtils';

const ViewDepartment = forwardRef(({ departmentId, onBack, editMode = false, createMode = false, onSave, onCancel, onEdit, onDepartmentCreated }, ref) => {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const [department, setDepartment] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const { showSuccess, showError, showWarning } = useMessages();

  useImperativeHandle(ref, () => ({
    handleDelete,
    handleSave
  }));

  useEffect(() => {
    if (departmentId && !createMode) {
      fetchDepartment();
    } else if (createMode) {
      // Initialize with default values for new department
      const newDepartment = {
        department_code: '',
        department_name: '',
        description: '',
        is_active: true
      };
      setDepartment(newDepartment);
      setEditData(newDepartment);
    }
  }, [departmentId, createMode]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (editMode || createMode) {
          handleCancel();
        } else {
          // In view mode, go back to list
          if (onBack) onBack();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editMode, createMode, onBack]);

  const fetchDepartment = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl(`/api/departments/${departmentId}`));
      if (response.ok) {
        const data = await response.json();
        setDepartment(data);
        setEditData(data);
      } else {
        setError(t('departments.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching department:', error);
      setError(t('departments.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!editData.department_code || !editData.department_name) {
        showError(t('departments.view.validation.required'));
        return;
      }

      // Prepare data for API
      const saveData = {
        department_code: editData.department_code,
        department_name: editData.department_name,
        description: editData.description,
        is_active: editData.is_active
      };

      const url = createMode
        ? getApiUrl('/api/departments/')
        : getApiUrl(`/api/departments/${departmentId}`);

      const method = createMode ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const updatedDepartment = await response.json();
        setDepartment(updatedDepartment);
        setEditData(updatedDepartment);
        setHasChanges(false);

        if (createMode) {
          showSuccess(t('departments.view.messages.createdSuccess'));
          if (onDepartmentCreated) {
            onDepartmentCreated(updatedDepartment.department_id);
          }
        } else {
          showSuccess(t('departments.view.messages.updatedSuccess'));
        }

        // Call onSave callback if provided
        if (onSave) {
          onSave();
        }
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error saving department:', error);
      showError(tCommon('errors.genericTitle'), t('departments.view.error.loadingError'));
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm(t('departments.view.confirmations.unsavedChanges'))) {
        setEditData(department || {});
        setHasChanges(false);
        if (onCancel) onCancel();
      }
    } else {
      if (onCancel) onCancel();
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('departments.view.confirmations.deleteConfirm', { name: department.department_name }))) {
      return;
    }

    try {
      const response = await apiFetch(getApiUrl(`/api/departments/${departmentId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        showSuccess(t('departments.view.messages.deletedSuccess'));
        if (onBack) onBack();
      } else {
        await showApiError(response, showError, tCommon);
      }
    } catch (error) {
      console.error('Error deleting department:', error);
      showError(tCommon('errors.genericTitle'), t('departments.view.error.fetchFailed'));
    }
  };

  if (loading) {
    return <div className="view-container"><div className="loading">{t('departments.loading')}</div></div>;
  }

  if (error && !department) {
    const errorMessage = typeof error === 'string' ? error : 'An error occurred while loading the department';
    return <div className="view-container"><div className="error-message">{errorMessage}</div></div>;
  }

  if (!department && !createMode) {
    return <div className="view-container"><div className="no-data">{t('departments.view.titles.noDepartmentFound')}</div></div>;
  }

  return (
    <div className="view-container">
      <div className="view-header">
        <div className="header-title">
          <h2>
            {createMode ? t('departments.view.titles.newDepartment') : department?.department_name || t('departments.view.titles.viewDepartment')}
          </h2>
          <span className="header-id">{!createMode && department?.department_code}</span>
        </div>
        <div className="header-status">
          {editData.is_active ? (
            <span className="status-badge status-active">{t('departments.filters.status.active')}</span>
          ) : (
            <span className="status-badge status-inactive">{t('departments.filters.status.inactive')}</span>
          )}
        </div>
      </div>

      <div className="view-content">
        <div className="form-section">
          <h3>{t('departments.view.sections.departmentInfo')}</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('departments.view.fields.code')} *</label>
              <input
                type="text"
                value={editData.department_code || ''}
                onChange={(e) => handleFieldChange('department_code', e.target.value.toUpperCase())}
                disabled={!editMode && !createMode}
                required
                maxLength="20"
              />
            </div>
            <div className="form-group">
              <label>{t('departments.view.fields.name')} *</label>
              <input
                type="text"
                value={editData.department_name || ''}
                onChange={(e) => handleFieldChange('department_name', e.target.value)}
                disabled={!editMode && !createMode}
                required
              />
            </div>
            <div className="form-group full-width">
              <label>{t('departments.view.fields.description')}</label>
              <textarea
                value={editData.description || ''}
                onChange={(e) => handleFieldChange('description', e.target.value)}
                disabled={!editMode && !createMode}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>{t('departments.view.fields.status')}</label>
              <select
                value={editData.is_active ? 'active' : 'inactive'}
                onChange={(e) => handleFieldChange('is_active', e.target.value === 'active')}
                disabled={!editMode && !createMode}
              >
                <option value="active">{t('departments.filters.status.active')}</option>
                <option value="inactive">{t('departments.filters.status.inactive')}</option>
              </select>
            </div>
          </div>
        </div>

        {department && department.created_date && (
          <div className="form-section">
            <h3>{t('departments.view.sections.systemInfo')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('departments.view.fields.createdDate')}</label>
                <span>{formatAppDate(department.created_date, 'datetime')}</span>
              </div>
              {department.updated_date && (
                <div className="info-item">
                  <label>{t('departments.view.fields.lastUpdated')}</label>
                  <span>{formatAppDate(department.updated_date, 'datetime')}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default ViewDepartment;